import Cookies from "js-cookie";
import makeApiCall from "../master/axiosIndex";

// All endpoints are mounted under /gateWay/agent/* on the gateway service.
// Auth is via the existing session cookie + Authorization header (same pattern
// the rest of the app uses through makeApiCall).

const AGENT_BASE = "/agent";

// ----- Types -----

export interface AgentVerifyResponse {
  ok: boolean;
  client_id?: string;
  org_id?: string;
  agent?: string;
  agent_access?: boolean;
  features?: {
    web_search_enabled?: boolean;
    web_search_supplier_names?: boolean;
    rate_limit_per_min?: number;
  };
  request_id?: string;
}

export interface AnalysisHistoryItem {
  analysis_id: string;
  version: number;
  is_latest: boolean;
  generated_at?: string;
  model_used?: string;
  cost_usd?: number;
  input_tokens?: number;
  output_tokens?: number;
  requested_by?: string;
}

export interface AnalysisHistoryResponse {
  tender_id: string;
  n_versions: number;
  versions: AnalysisHistoryItem[];
}

export interface DataFreshnessLastSync {
  batch_id?: string;
  status?: string;
  started_at_utc?: string;
  completed_at_utc?: string;
  started_at_ist?: string;
  completed_at_ist?: string;
  duration_seconds?: number;
  minutes_ago?: number;
  human?: string;
  entity_count?: number;
  records_extracted?: number;
  records_loaded?: number;
  records_rejected?: number;
}

export interface DataFreshnessResponse {
  client_id?: string;
  tenant_prefix?: string;
  schedule?: string;
  interval_minutes?: number;
  last_sync?: DataFreshnessLastSync | null;
  currently_running?: any;
  next_run_estimated_utc?: string;
  next_run_estimated_ist?: string;
  checked_at_ist?: string;
}

export interface ChatSession {
  session_id: string;
  client_id?: string;
  tender_id?: string | null;
  report_id?: string | null;
  title?: string;
  created_at?: string;
  updated_at?: string;
  closed_at?: string | null;
}

export interface ChatHistoryMessage {
  message_id: string;
  role: "user" | "assistant";
  content: string;
  cited_chunk_ids?: string[];
  cited_silver_rows?: string[];
  cited_web_urls?: string[];
  cited_sources?: Array<{ filename: string; url?: string; doc_id?: string }>;
  suggested_questions?: string[];
  token_usage?: { input?: number; output?: number };
  cost_usd?: number;
  deployment_name?: string;
  created_at?: string;
}

// SSE event payloads sent by /chat/sessions/:id/messages
export type ChatStreamEvent =
  | { type: "file_uploaded"; data: { filename: string; doc_id: string; status: string } }
  | { type: "user_message_persisted"; data: { message_id: string } }
  | { type: "tool_call"; data: { name: string; arguments: any } }
  | { type: "tool_result"; data: { name: string; size?: number } }
  | { type: "assistant_delta"; data: { text: string } }
  | {
      type: "assistant_done";
      data: {
        message_id: string;
        text: string;
        citations?: {
          chunks?: string[];
          silver?: any[];
          urls?: string[];
          // Structured per-source metadata used by the inline citation
          // chips ("(Source: foo.txt)" -> downloadable pill).
          sources?: Array<{
            filename: string;
            url?: string;
            doc_id?: string;
          }>;
        };
        suggested_questions?: string[];
      };
    }
  | { type: "error"; data: any };

// ----- Helpers for streaming SSE -----

// Build a token-aware fetch init that matches the existing axios behaviour
// (Authorization header from the access-token cookie, credentials included).
function authFetchInit(
  method: "GET" | "POST",
  body?: BodyInit | null,
  extraHeaders: Record<string, string> = {}
): RequestInit {
  const accessToken = Cookies.get("access-token");
  const headers: Record<string, string> = { ...extraHeaders };
  if (accessToken) headers["Authorization"] = `${accessToken}`;
  return {
    method,
    credentials: "include",
    headers,
    body: body ?? null,
  };
}

function resolveBaseUrl(): string {
  // Allow override for the agent gateway, otherwise fall back to the main API base.
  return (
    (process.env.REACT_APP_AGENT_GATEWAY_URL as string | undefined) ||
    (process.env.REACT_APP_API_LOGIN_URL as string | undefined) ||
    ""
  );
}

function agentUrl(path: string): string {
  // path is everything after /gateWay/agent (e.g. "/chat/sessions/foo/messages")
  const base = resolveBaseUrl().replace(/\/$/, "");
  return `${base}${AGENT_BASE}${path}`;
}

// Stream an SSE response by reading the body incrementally. Each parsed event is
// emitted to `onEvent`. The promise resolves when the stream ends (or rejects on
// transport / non-2xx errors). Callers can pass an AbortSignal via init.
export async function streamSSE(
  url: string,
  init: RequestInit,
  onEvent: (evt: ChatStreamEvent) => void
): Promise<void> {
  const res = await fetch(url, init);
  if (!res.ok || !res.body) {
    let msg = `HTTP ${res.status}`;
    try {
      const txt = await res.text();
      msg = txt || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  // SSE frames are separated by a blank line. A frame can have multiple "event:"
  // and "data:" lines. We accumulate text until we see "\n\n".
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sepIdx = buffer.indexOf("\n\n");
    while (sepIdx !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      const parsed = parseSseFrame(frame);
      if (parsed) onEvent(parsed);
      sepIdx = buffer.indexOf("\n\n");
    }
  }
  // Flush trailing frame (no terminating blank line)
  if (buffer.trim()) {
    const parsed = parseSseFrame(buffer);
    if (parsed) onEvent(parsed);
  }
}

function parseSseFrame(frame: string): ChatStreamEvent | null {
  let eventName: string | null = null;
  const dataLines: string[] = [];
  for (const raw of frame.split("\n")) {
    const line = raw.trimEnd();
    if (!line) continue;
    if (line.startsWith(":")) continue; // comment
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
  }
  if (!eventName) return null;
  const dataText = dataLines.join("\n");
  let data: any = dataText;
  try {
    if (dataText) data = JSON.parse(dataText);
  } catch {
    /* leave as raw string */
  }
  return { type: eventName as any, data } as ChatStreamEvent;
}

// ----- Service -----

const agentService = {
  // 1. Auth verify
  verifyAgent: (): Promise<AgentVerifyResponse> => {
    return makeApiCall({
      url: `${AGENT_BASE}/auth/verify`,
      method: "get",
    });
  },

  // 2. Tender analysis (instant SQL) — quick deterministic dashboard
  getQuickAnalysis: (tenderId: string | number): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/tenders/${tenderId}/analysis`,
      method: "get",
    });
  },

  // 3. AI analysis (LLM, cached) — cold ~30s, cached ~1s. Path is :tenderId.
  getAIAnalysis: (tenderId: string | number): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/tenders/${tenderId}/ai-analysis`,
      method: "get",
    });
  },

  // 4. AI analysis history (version picker)
  getAnalysisHistory: (
    tenderId: string | number,
    limit?: number
  ): Promise<AnalysisHistoryResponse | any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/tenders/${tenderId}/ai-analysis/history`,
      method: "get",
      params: limit ? { limit } : undefined,
    });
  },

  // 5. Regenerate AI analysis (~30s, $0.05). Throttle in the UI.
  regenerateAnalysis: (tenderId: string | number): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/tenders/${tenderId}/ai-analysis/regenerate`,
      method: "post",
    });
  },

  // 6. Create chat session (optionally pinned to a tender / report)
  createChatSession: (body: {
    tender_id?: string | number | null;
    report_id?: string | null;
    title?: string;
  }): Promise<ChatSession> => {
    return makeApiCall({
      url: `${AGENT_BASE}/chat/sessions`,
      method: "post",
      data: body,
    });
  },

  // 7. List chat sessions
  listChatSessions: (params?: {
    limit?: number;
    tender_id?: string | number;
  }): Promise<{ client_id: string; sessions: ChatSession[] }> => {
    return makeApiCall({
      url: `${AGENT_BASE}/chat/sessions`,
      method: "get",
      params,
    });
  },

  // 8. Send chat message (SSE) — text-only JSON variant.
  // Caller must process events via the provided onEvent callback.
  sendChatMessageText: async (
    sessionId: string,
    payload: { content: string; stream?: boolean },
    onEvent: (evt: ChatStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const url = agentUrl(`/chat/sessions/${sessionId}/messages`);
    const init = authFetchInit("POST", JSON.stringify({ stream: true, ...payload }), {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    });
    (init as any).signal = signal;
    return streamSSE(url, init, onEvent);
  },

  // 8b. Send chat message (SSE) — multipart variant with optional files.
  sendChatMessageMultipart: async (
    sessionId: string,
    fields: {
      content: string;
      stream?: boolean;
      tender_id?: string | number;
      embed_sync?: boolean;
      files?: File[];
    },
    onEvent: (evt: ChatStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const url = agentUrl(`/chat/sessions/${sessionId}/messages`);
    const fd = new FormData();
    fd.append("content", fields.content);
    fd.append("stream", String(fields.stream ?? true));
    if (fields.tender_id != null) fd.append("tender_id", String(fields.tender_id));
    if (fields.embed_sync != null) fd.append("embed_sync", String(fields.embed_sync));
    if (fields.files) {
      for (const f of fields.files) fd.append("files", f, f.name);
    }
    const init = authFetchInit("POST", fd, { Accept: "text/event-stream" });
    (init as any).signal = signal;
    return streamSSE(url, init, onEvent);
  },

  // 9. Chat history (replay messages for a session)
  getChatHistory: (
    sessionId: string
  ): Promise<{
    session_id: string;
    client_id: string;
    session: ChatSession;
    messages: ChatHistoryMessage[];
  }> => {
    return makeApiCall({
      url: `${AGENT_BASE}/chat/sessions/${sessionId}/history`,
      method: "get",
    });
  },

  // 10. List documents
  listDocs: (params?: {
    tender_id?: string | number;
    limit?: number;
  }): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/docs`,
      method: "get",
      params,
    });
  },

  // 11. Get one document (includes signed blob_url)
  getDoc: (docId: string): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/docs/${docId}`,
      method: "get",
    });
  },

  // 12. Upload documents (multipart). Returns 202 with per-file status.
  uploadDocs: async (
    files: File[],
    tenderId?: string | number
  ): Promise<any> => {
    const fd = new FormData();
    for (const f of files) fd.append("file", f, f.name);
    if (tenderId != null) fd.append("tender_id", String(tenderId));
    return makeApiCall({
      url: `${AGENT_BASE}/docs/upload`,
      method: "post",
      data: fd,
    });
  },

  // 13. Usage summary
  getUsageSummary: (days?: number): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/usage/summary`,
      method: "get",
      params: days ? { days } : undefined,
    });
  },

  // 14. Usage for one chat session
  getSessionUsage: (sessionId: string): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/usage/sessions/${sessionId}`,
      method: "get",
    });
  },

  // 15. Data freshness — last MySQL → silver/gold sync timestamp + schedule.
  // Used to render a "Data refreshed N minutes ago" badge in dashboards.
  getDataFreshness: (): Promise<DataFreshnessResponse> => {
    return makeApiCall({
      url: `${AGENT_BASE}/system/data-freshness`,
      method: "get",
    });
  },

  // 16. Submit feedback (👍 / 👎) on an assistant chat message. Idempotent —
  // posting again with the same (messageId, user) updates the existing row,
  // so toggling between good/bad works without an explicit DELETE.
  submitMessageFeedback: (
    messageId: string,
    body: { rating: "good" | "bad"; reason?: string }
  ): Promise<any> => {
    return makeApiCall({
      url: `${AGENT_BASE}/chat/messages/${messageId}/feedback`,
      method: "post",
      data: body,
    });
  },
};

export default agentService;
